import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { App, Alert, Button, Drawer, Input, Space, Typography } from 'antd';
import { RocketOutlined } from '@ant-design/icons';
import { getTemplate, instantiateTemplate, extractInstantiateDiagnostics } from '@/api/templates';
import { listScreenTemplates, type ScreenTemplateSummary } from '@/api/screens';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import type { ServerDiagnostic, TemplateDetail } from '@/api/types';
import { ulid } from '@/api/ulid';
import { IrPreview } from '@/components/IrPreview';
import { CategoryBadge } from './categoryBadge';

const { Paragraph, Text } = Typography;

function slugify(name: string): string {
  const base = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32);
  return `${base || 'flow'}-${ulid().slice(-6).toLowerCase()}`;
}

function DiagnosticList({ diagnostics }: { diagnostics: ServerDiagnostic[] }) {
  return (
    <div>
      {diagnostics.map((d, i) => (
        <div key={i} className="ol-problem" style={{ cursor: 'default' }}>
          <span
            aria-hidden
            style={{
              color: `var(--ol-${d.severity === 'ERROR' ? 'negative' : d.severity === 'WARNING' ? 'attention' : 'info'})`,
            }}
          >
            {d.severity === 'ERROR' ? '●' : d.severity === 'WARNING' ? '▲' : 'ℹ'}
          </span>
          <span>
            <span className="ol-problem__code">{d.code}</span> {d.message}
            {d.nodeId && (
              <Text type="secondary" className="ol-mono" style={{ fontSize: 10, marginLeft: 6 }}>
                @{d.nodeId}
              </Text>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

/** B4 — template detail + instantiate (a COPY of the IR) → a new DRAFT flow in the PS-2 designer. */
export function TemplateDrawer({
  templateId,
  onClose,
}: {
  templateId: string | null;
  onClose: () => void;
}) {
  const { message } = App.useApp();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<TemplateDetail | null>(null);
  const [loading, setLoading] = useState(false);
  // The applicant journeys made for this flow template (OL-24). Absent is not an error.
  const [screenTemplates, setScreenTemplates] = useState<ScreenTemplateSummary[]>([]);
  useEffect(() => {
    if (!templateId) {
      setScreenTemplates([]);
      return;
    }
    listScreenTemplates(templateId)
      .then(setScreenTemplates)
      .catch(() => setScreenTemplates([]));
  }, [templateId]);
  const [unavailable, setUnavailable] = useState(false);

  const [flowId, setFlowId] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [failDiagnostics, setFailDiagnostics] = useState<ServerDiagnostic[] | null>(null);
  const [irUnavailableMsg, setIrUnavailableMsg] = useState<string | null>(null);
  // ONE idempotency key per drawer-open/template, reused across instantiate RETRIES so a retry
  // replays the original result instead of creating a second flow. Regenerated only after a
  // success or a template change.
  const [idemKey, setIdemKey] = useState<string>(() => ulid());

  const load = useCallback(async (id: string) => {
    setLoading(true);
    setUnavailable(false);
    setDetail(null);
    setFailDiagnostics(null);
    setIrUnavailableMsg(null);
    setIdemKey(ulid()); // a new template = a new logical operation
    try {
      const d = await getTemplate(id);
      setDetail(d);
      setName(d.name ?? '');
      setFlowId(slugify(d.name ?? id));
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) setUnavailable(true);
      else message.error(n.message);
    } finally {
      setLoading(false);
    }
    // message stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (templateId) void load(templateId);
  }, [templateId, load]);

  const instantiate = useCallback(async () => {
    if (!detail || !flowId.trim()) return;
    setBusy(true);
    setFailDiagnostics(null);
    setIrUnavailableMsg(null);
    try {
      const res = await instantiateTemplate(
        detail.templateId,
        {
          flowId: flowId.trim(),
          name: name.trim() || undefined,
        },
        idemKey,
      );
      setIdemKey(ulid()); // the operation succeeded — the next instantiate is a new operation
      message.success(`Created draft flow “${res.flowId}” from template.`);
      onClose();
      navigate(`/flows/${encodeURIComponent(res.flowId)}`);
    } catch (e) {
      const n = normalizeApiError(e);
      if (n.code === 'TEMPLATE_IR_UNAVAILABLE') {
        // NOT a compile failure: this deployment has no legacy corpus mounted, so the estate
        // candidate's IR cannot be resolved at all. Teach, don't show compiler diagnostics.
        setIrUnavailableMsg(
          'The legacy corpus is not mounted on this deployment, so this estate candidate’s IR ' +
            'cannot be resolved. Curated templates still work; ask an operator to mount the ' +
            'corpus (opsflow.product-api.migration.corpus-root) to use estate candidates.',
        );
        message.warning('Template IR unavailable on this deployment.');
      } else if (n.code === 'TEMPLATE_COMPILE_FAILED' || n.httpStatus === 422) {
        // Prefer diagnostics off the error body; fall back to the template's own diagnostics.
        const fromErr = extractInstantiateDiagnostics(e);
        setFailDiagnostics(fromErr.length ? fromErr : (detail.diagnostics ?? []));
        message.error('This template did not compile. See the diagnostics below.');
      } else if (isNotAvailable(n)) {
        message.info('The template-instantiate endpoint is not live yet (Track A pending).');
      } else {
        message.error(n.message);
      }
    } finally {
      setBusy(false);
    }
  }, [detail, flowId, name, idemKey, message, navigate, onClose]);

  const isEstate = detail?.category?.toUpperCase?.() === 'ESTATE_CANDIDATE';

  return (
    <Drawer
      open={Boolean(templateId)}
      onClose={onClose}
      width={640}
      loading={loading}
      title={detail ? detail.name : 'Template'}
    >
      {unavailable && (
        <Alert
          type="info"
          showIcon
          message="Template detail is not live yet (Track A template API pending)."
        />
      )}
      {detail && (
        <div>
          <Space wrap style={{ marginBottom: 12 }}>
            <CategoryBadge category={detail.category} />
            {detail.jurisdiction && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {detail.jurisdiction}
              </Text>
            )}
            {typeof detail.nodeCount === 'number' && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {detail.nodeCount} nodes
              </Text>
            )}
            {detail.sourceWorkflowId && (
              <Text className="ol-mono" style={{ fontSize: 10.5, color: 'var(--ol-text-tertiary)' }}>
                src: {detail.sourceWorkflowId}
              </Text>
            )}
          </Space>
          {screenTemplates.length > 0 && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="A ready applicant journey fits this flow"
              description={screenTemplates.map((t) => (
                <div key={t.templateId}>
                  <Link to={`/screens?template=${encodeURIComponent(t.templateId)}`}>{t.name}</Link>
                </div>
              ))}
            />
          )}

          {detail.description && (
            <Paragraph style={{ fontSize: 13, lineHeight: 1.6 }}>{detail.description}</Paragraph>
          )}

          {isEstate && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Not yet SIGNAL-reviewed"
              description="This template was harvested from the demo estate. It has not been through regulatory (SIGNAL) review and may carry non-canonical capability families. Review it in the designer before publishing."
            />
          )}

          {detail.regNotes && (
            <div style={{ marginBottom: 14 }}>
              <div className="ol-section">Regulatory notes</div>
              <div
                style={{
                  fontSize: 12.5,
                  lineHeight: 1.6,
                  color: 'var(--ol-text-secondary)',
                  borderLeft: '3px solid var(--ol-attention)',
                  paddingLeft: 12,
                }}
              >
                {detail.regNotes}
              </div>
            </div>
          )}

          {detail.diagnostics && detail.diagnostics.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div className="ol-section">
                Diagnostics <span className="ol-section__count">{detail.diagnostics.length}</span>
              </div>
              <DiagnosticList diagnostics={detail.diagnostics} />
            </div>
          )}

          <div className="ol-section">Flow preview</div>
          <div style={{ marginTop: 6, marginBottom: 16 }}>
            <IrPreview irPreview={detail.irPreview} />
          </div>

          <div style={{ borderTop: '1px solid var(--ol-border)', paddingTop: 14 }}>
            <div className="ol-section">Use this template</div>
            <Paragraph type="secondary" style={{ fontSize: 12 }}>
              Creates a new DRAFT flow as a copy of this template’s IR and opens it in the designer.
              Nothing is published — you review and submit for approval there.
            </Paragraph>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 3 }}>New flow id</div>
                <Input
                  value={flowId}
                  onChange={(e) => setFlowId(e.target.value)}
                  className="ol-mono"
                  placeholder="new-flow-id"
                />
              </label>
              <label>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 3 }}>Name</div>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Flow name" />
              </label>
            </div>

            {irUnavailableMsg && (
              <div style={{ marginTop: 12 }}>
                <Alert
                  type="warning"
                  showIcon
                  message="Template IR unavailable on this deployment"
                  description={irUnavailableMsg}
                />
              </div>
            )}

            {failDiagnostics && (
              <div style={{ marginTop: 12 }}>
                <Alert
                  type="error"
                  showIcon
                  message="Template compile failed"
                  description={
                    failDiagnostics.length ? (
                      <div style={{ marginTop: 6 }}>
                        <DiagnosticList diagnostics={failDiagnostics} />
                        <Text type="secondary" style={{ fontSize: 11.5 }}>
                          Estate candidates can carry non-canonical families the compiler rejects. Open a
                          curated template, or ask an admin to true-up the family mapping.
                        </Text>
                      </div>
                    ) : (
                      'The template could not be compiled to a valid flow. It may carry non-canonical families.'
                    )
                  }
                />
              </div>
            )}

            <Button
              type="primary"
              icon={<RocketOutlined />}
              loading={busy}
              disabled={!flowId.trim()}
              onClick={instantiate}
              style={{ marginTop: 14 }}
              block
            >
              Use this template
            </Button>
          </div>
        </div>
      )}
    </Drawer>
  );
}
